/**
 * Bell icon + notifications panel. Lives in the app shell so it's
 * always visible regardless of phase / route.
 *
 * Polls /api/me/notifications/unread every 25s when the tab is visible
 * (cheap, no socket needed). Click a notification to mark it read AND
 * navigate to its href. "Ack" is a separate endpoint reserved for the
 * service worker (cancels the digest email even before the user opens
 * the app).
 */

import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { Modal } from './Modal';
import { IconBell, IconCheck } from './Icon';
import { Spinner } from './Spinner';
import { UiNode } from '../lib/UiNode';
import { useRouter } from '../router';

interface NotifRow {
  id: string;
  boardId: string | null;
  actorId: string | null;
  kind: string;
  summary: string;
  href: string | null;
  readAt: number | null;
  createdAt: number;
}

const POLL_INTERVAL_MS = 25_000;

export function NotificationsBell() {
  const [unread, setUnread] = useState(0);
  // Trigger a wobble animation on count INCREASE only — silent
  // decrease (mark-read) shouldn't animate. We re-key the badge so
  // the keyframe restarts each time.
  const prevUnreadRef = useRef(0);
  const [badgeKey, setBadgeKey] = useState(0);
  useEffect(() => {
    if (unread > prevUnreadRef.current && prevUnreadRef.current > 0) {
      setBadgeKey((k) => k + 1);
    }
    prevUnreadRef.current = unread;
  }, [unread]);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotifRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  async function poll(): Promise<void> {
    try {
      const r = await api.unreadNotifications();
      setUnread(r.unread);
    } catch {
      /* swallow; bell continues to function */
    }
  }

  useEffect(() => {
    void poll();
    const tick = () => {
      if (document.visibilityState === 'visible') void poll();
    };
    timerRef.current = setInterval(tick, POLL_INTERVAL_MS);
    const onVis = () => {
      if (document.visibilityState === 'visible') void poll();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  async function openPanel() {
    setOpen(true);
    setError(null);
    try {
      const r = await api.notifications();
      setItems(r.notifications);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    }
  }

  return (
    <>
      <UiNode
        as="button"
        name="app-bell-button"
        entity="notifications"
        action="open"
        state={unread > 0 ? 'unread' : 'idle'}
        type="button"
        className="btn btn-ghost relative inline-flex items-center gap-1.5"
        onClick={openPanel}
        aria-label="Notifications"
        title="Notifications"
      >
        <IconBell size={16} />
        {unread > 0 && (
          <span
            key={badgeKey}
            aria-label={`${unread} unread notification${unread === 1 ? '' : 's'}`}
            aria-live="polite"
            role="status"
            className={`${badgeKey === 0 ? 'pop-in' : 'badge-nudge'} absolute -right-1 -top-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none`}
            style={{
              background: 'var(--color-clove)',
              color: 'var(--color-ink)',
              boxShadow:
                '0 0 0 2px var(--color-ink), 0 0 12px -2px color-mix(in oklab, var(--color-clove) 70%, transparent)',
            }}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </UiNode>

      <NotificationsPanel
        open={open}
        onClose={() => setOpen(false)}
        items={items}
        error={error}
        onMarkAll={async () => {
          await api.markAllNotificationsRead();
          setItems((cur) =>
            (cur ?? []).map((n) =>
              n.readAt ? n : { ...n, readAt: Math.floor(Date.now() / 1000) },
            ),
          );
          setUnread(0);
        }}
        onItemClick={async (n) => {
          await api.markNotificationRead(n.id);
          setItems((cur) =>
            (cur ?? []).map((x) =>
              x.id === n.id ? { ...x, readAt: Math.floor(Date.now() / 1000) } : x,
            ),
          );
          setUnread((u) => Math.max(0, u - (n.readAt ? 0 : 1)));
        }}
      />
    </>
  );
}

function NotificationsPanel({
  open,
  onClose,
  items,
  error,
  onMarkAll,
  onItemClick,
}: {
  open: boolean;
  onClose: () => void;
  items: NotifRow[] | null;
  error: string | null;
  onMarkAll: () => Promise<void>;
  onItemClick: (n: NotifRow) => Promise<void>;
}) {
  const { navigate } = useRouter();
  const hasAnyUnread = (items ?? []).some((n) => !n.readAt);
  return (
    <Modal open={open} onClose={onClose} title="Notifications" maxWidth={520}>
      <div className="flex max-h-[70vh] flex-col gap-2">
        {hasAnyUnread && (
          <UiNode
            as="button"
            name="app-bell-mark-all-read"
            entity="notifications"
            action="mark_all_read"
            type="button"
            className="btn btn-ghost ml-auto inline-flex items-center gap-1.5 text-xs"
            onClick={onMarkAll}
          >
            <IconCheck size={12} />
            Mark all as read
          </UiNode>
        )}
        {!items ? (
          <div className="flex items-center gap-2 py-2 text-xs text-cream-3">
            <Spinner size={12} />
            <span>Loading notifications…</span>
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-cream-3">All quiet here.</p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {items.map((n) => {
              const unread = !n.readAt;
              return (
                <li key={n.id}>
                  <UiNode
                    as="button"
                    name="app-bell-notification-row"
                    entity="notification"
                    entityId={n.id}
                    action="open"
                    state={unread ? 'unread' : 'read'}
                    type="button"
                    className="row-nudge flex w-full items-start gap-2 rounded-lg p-2 text-left hover:bg-ink-3"
                    style={{ background: unread ? 'var(--color-clove-3)' : 'transparent' }}
                    onClick={async () => {
                      await onItemClick(n);
                      if (n.href) {
                        onClose();
                        navigate(n.href);
                      }
                    }}
                  >
                    <span
                      className="mt-1.5 inline-block h-1.5 w-1.5 rounded-full"
                      style={{ background: unread ? 'var(--color-clove)' : 'transparent' }}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm">{n.summary}</p>
                      <p className="text-[11px] text-cream-3">{relative(n.createdAt)}</p>
                    </div>
                  </UiNode>
                </li>
              );
            })}
          </ul>
        )}
        {error && <p className="text-xs text-rust">{error}</p>}
      </div>
    </Modal>
  );
}

function relative(unixSec: number): string {
  const d = Math.floor(Date.now() / 1000) - unixSec;
  if (d < 60) return 'just now';
  if (d < 3600) return `${Math.floor(d / 60)}m`;
  if (d < 86400) return `${Math.floor(d / 3600)}h`;
  return `${Math.floor(d / 86400)}d`;
}
